import { api, tokens } from "../lib/api.js";

/**
 * The account gate.
 *
 * This is the first surface a streamer ever sees, so it carries the things a
 * bare email/password box leaves out: what signing up actually costs (nothing,
 * for 14 days), that a verification email is coming and why it matters (the
 * trial does not start until it is clicked), and an explicit record of consent
 * to the terms — which is a legal requirement, not decoration, for a service
 * that hosts other people's video.
 */

const esc = (s: string) =>
  s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

export function loginView(root: HTMLElement, onDone: () => void) {
  let mode: "login" | "register" = "login";

  function draw() {
    const register = mode === "register";
    root.innerHTML = `
      <div class="gate">
        <div class="gate-card">
          <span class="gate-brand"><span class="lamp" aria-hidden="true"></span>Stream-Cut</span>

          <h1>${register ? "Create your account" : "Welcome back"}</h1>
          <p class="gate-sub">${
            register
              ? "14 days free, and no card to start. Long enough to see a stream come back as clips, twice."
              : "Sign in to your recordings and clips."
          }</p>

          <form id="form" novalidate>
            <label>Email
              <input type="email" id="email" required autocomplete="email"
                     placeholder="you@example.com">
            </label>

            <label>Password
              <input type="password" id="password" required minlength="10"
                     autocomplete="${register ? "new-password" : "current-password"}"
                     placeholder="${register ? "At least 10 characters" : ""}">
            </label>

            ${
              register
                ? `<label class="consent">
                     <input type="checkbox" id="consent" required>
                     <span>I have read and agree to the <a href="https://stream-cut.com/terms/"
                     target="_blank" rel="noopener">Terms of Service</a>, the
                     <a href="https://stream-cut.com/privacy/" target="_blank"
                     rel="noopener">Privacy Policy</a> and the
                     <a href="https://stream-cut.com/refund/" target="_blank"
                     rel="noopener">Refund Policy</a>, and I confirm I have the right to use
                     the video I upload.</span>
                   </label>`
                : ""
            }

            <p class="error" id="error" hidden></p>

            <button class="btn primary" type="submit" id="submit">
              ${register ? "Create account" : "Sign in"}
            </button>
            ${register ? "" : `<p class="gate-foot"><a href="#/forgot">Forgot your password?</a></p>`}
          </form>

          ${
            register
              ? `<div class="gate-note">
                   <span aria-hidden="true">✉</span>
                   <span>We'll email you a link to confirm your address. Your 14 days start
                   when you click it — not now — so there's no rush.</span>
                 </div>`
              : ""
          }

          <p class="gate-foot">
            ${register ? "Already have an account?" : "New to Stream-Cut?"}
            <button class="link" type="button" id="toggle">
              ${register ? "Sign in" : "Create an account"}
            </button>
          </p>
        </div>
      </div>`;

    const form = root.querySelector<HTMLFormElement>("#form")!;
    const error = root.querySelector<HTMLElement>("#error")!;
    const submit = root.querySelector<HTMLButtonElement>("#submit")!;
    const emailEl = root.querySelector<HTMLInputElement>("#email")!;
    const passEl = root.querySelector<HTMLInputElement>("#password")!;
    const consentEl = root.querySelector<HTMLInputElement>("#consent");

    root.querySelector("#toggle")!.addEventListener("click", () => {
      mode = register ? "login" : "register";
      draw();
    });

    const fail = (message: string) => {
      error.textContent = message;
      error.hidden = false;
      submit.disabled = false;
      submit.textContent = register ? "Create account" : "Sign in";
    };

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      error.hidden = true;

      // Validated here rather than relying on the browser, so the message sits
      // with the rest of the form's errors instead of in a native bubble.
      if (!emailEl.value.includes("@")) return fail("Enter a valid email address.");
      if (passEl.value.length < 10) return fail("Passwords need at least 10 characters.");
      if (register && consentEl && !consentEl.checked) {
        return fail("Please accept the Terms of Service, Privacy Policy and Refund Policy to continue.");
      }

      submit.disabled = true;
      submit.textContent = register ? "Creating account…" : "Signing in…";

      try {
        const body: Record<string, unknown> = {
          email: emailEl.value.trim(),
          password: passEl.value,
        };
        // Consent is sent so it can be recorded against the account; a claim
        // nobody stored is not a claim you can rely on later.
        if (register) body.accepted_terms = true;

        const r = await api<{ access_token: string; refresh_token: string }>(
          "POST",
          register ? "/v1/auth/register" : "/v1/auth/login",
          body,
        );
        tokens.set(r.access_token, r.refresh_token);

        if (register) return sent(emailEl.value.trim());
        onDone();
      } catch (err) {
        fail(err instanceof Error ? err.message : "Something went wrong.");
      }
    });
  }

  /**
   * Post-signup. The account exists and they are signed in, but the trial is
   * waiting on the verification click — so say that plainly here rather than
   * dropping them into the dashboard to discover a banner.
   */
  function sent(email: string) {
    root.innerHTML = `
      <div class="gate">
        <div class="gate-card gate-sent">
          <div class="empty-icon" aria-hidden="true">✉</div>
          <h1>Check your email</h1>
          <p class="gate-sub">We sent a confirmation link to <strong>${esc(email)}</strong>.
            Your 14 days start when you click it.</p>
          <button class="btn primary" id="go">Continue to the dashboard</button>
          <p class="gate-foot">No email after a minute? Check spam — or resend it
            from your Account page.</p>
        </div>
      </div>`;
    root.querySelector("#go")!.addEventListener("click", onDone);
  }

  draw();
}
